import { Box } from '@chakra-ui/react';
import { getIsVoidAndRefundEnabled } from '@melio/ap-domain';
import { useTheme } from '@melio/penny';
import {
  ApprovalDecision,
  PaymentApprovalDecisionStatusEnum,
  PaymentFullyExpanded,
  PaymentStatusEnum,
} from '@melio/platform-api';
import { useCallback } from 'react';

import {
  isPaymentCompletedRefund,
  isPaymentCompletedVoidAndRefund,
  isPaymentScheduledRefund,
  isPaymentScheduledVoidAndRefund,
  isPaymentScheduledVoidToRefund,
  isPaymentScheduledVoidToResend,
} from '../../../../utils/pay-flow/Payment.utils';
import { PaymentApprovalDeclined } from './approval-declined/PaymentApprovalDeclined';
import { PaymentCompleted } from './completed/PaymentCompleted';
import { RefundCompleted } from './completed/RefundCompleted';
import { PaymentFailed } from './failed/PaymentFailed';
import { PaymentInProgress } from './in-progress/PaymentInProgress';
import { RefundInProgress } from './in-progress/RefundInProgress';
import { PaymentScheduled } from './scheduled/PaymentScheduled';

export type PaymentDetailsProps = {
  payment: PaymentFullyExpanded;
  approvalDecisions?: ApprovalDecision[];
};

export const PaymentDetails = ({ payment, approvalDecisions }: PaymentDetailsProps) => {
  const { colors } = useTheme();
  const isVoidAndRefundEnabled = getIsVoidAndRefundEnabled(payment);

  const renderPaymentDetailsComponent = useCallback(() => {
    switch (payment.status) {
      case PaymentStatusEnum.Blocked:
      case PaymentStatusEnum.Scheduled:
        return <PaymentScheduled payment={payment} approvalDecisions={approvalDecisions} />;
      case PaymentStatusEnum.InProgress:
        return <PaymentInProgress payment={payment} approvalDecisions={approvalDecisions} />;
      case PaymentStatusEnum.Completed:
        return isVoidAndRefundEnabled ? (
          <PaymentInProgress payment={payment} approvalDecisions={approvalDecisions} />
        ) : (
          <PaymentCompleted payment={payment} approvalDecisions={approvalDecisions} />
        );
      case PaymentStatusEnum.Failed: {
        const isRefundInProgress = isPaymentScheduledRefund(payment);
        const isVoidAndRefundInProgress = isPaymentScheduledVoidAndRefund(payment);
        const isRefundCompleted = isPaymentCompletedRefund(payment);
        const isVoidAndRefundCompleted = isPaymentCompletedVoidAndRefund(payment);
        const isVoidingToResend = isPaymentScheduledVoidToResend(payment);
        const isVoidingToRefund = isPaymentScheduledVoidToRefund(payment);

        if (payment.approvalDecisionStatus === PaymentApprovalDecisionStatusEnum.Declined && approvalDecisions) {
          return <PaymentApprovalDeclined payment={payment} approvalDecisions={approvalDecisions} />;
        }
        if (isVoidingToResend) {
          return <PaymentInProgress payment={payment} approvalDecisions={approvalDecisions} />;
        }
        if (isVoidingToRefund) {
          return <RefundInProgress payment={payment} />;
        }
        if (isRefundInProgress || isVoidAndRefundInProgress) {
          return <RefundInProgress payment={payment} />;
        }
        if (isRefundCompleted || isVoidAndRefundCompleted) {
          return <RefundCompleted payment={payment} />;
        }

        return <PaymentFailed payment={payment} />;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  return (
    <Box border={`1px solid ${colors.neutral.light}`} borderRadius="8px" w="full">
      {renderPaymentDetailsComponent()}
    </Box>
  );
};
